import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ReplaySubject, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Product } from '../classes/utility';
import { EnvironmentService } from './environment.service';
import { LocalStorageService } from './localstorage.service';
import { SessionStorageService } from './sessionstorage.service';

@Injectable({
  providedIn: 'root',
})
export class WishlistService {
  wishlistApiUrl: string =
    'https://prod-api.wishlist.plutocracy.io/api/wishlist';

  private wishlistSubject: any = new ReplaySubject<any>(1);
  public wishlistObservable = this.wishlistSubject.asObservable();

  constructor(
    private http: HttpClient,
    private sessionStorage: SessionStorageService,
    private localStorage: LocalStorageService,
    private environmentService: EnvironmentService
  ) {
    if (this.environmentService.isBrowser()) {
      this.wishlistSubject.next(this.getWishlistFromsessionStorage());
    }
  }

  assembleWishlistApiUrl(pathname: 'event' | 'id' | 'email') {
    return `${this.wishlistApiUrl}/${pathname}/${environment.wishlistId}`;
  }

  itemInWishlist(
    productHandle: string,
    email?: string | null,
    id?: string | null
  ): boolean {
    let wishlist = this.getWishlistFromsessionStorage();
    id = id ? id : this.getWishlistIdFromStorage();
    email = email ? email : this.getEmailFromStorage();
    if (!wishlist) {
      if (email) {
        this.getWishlistByEmail(email);
      }
      if (!email && id) {
        this.getWishlistById(id);
      }
    }
    // clean this up -- should only be one layer of event body
    return wishlist?.events?.find(
      (x: any) => x?.eventBody?.productHandle === productHandle
    )
      ? true
      : false;
  }

  getWishlistIdFromStorage(): string | null {
    try {
      return this.localStorage?.getItem('fl-wishlist-id');
    } catch (err) {
      console.error('Could not get wishlistId from this.sessionStorage', err);
    }
    return null;
  }

  setWishlistIdInStorage(id: string) {
    try {
      this.localStorage.setItem('fl-wishlist-id', id);
    } catch (err) {
      console.error('Could not set wishlistId in this.sessionStorage');
    }
  }

  getEmailFromStorage(): string | null {
    try {
      return this.localStorage?.getItem('fl-wishlist-email') || null;
    } catch (err) {
      console.error(
        'Could not get fl-wishlist-email from this.sessionStorage',
        err
      );
      return null;
    }
  }

  setEmailInStorage(email: string) {
    if (!email) {
      console.warn('Email is falsey');
      return;
    }
    try {
      this.localStorage.setItem('fl-wishlist-email', email);
    } catch (err) {
      console.error('Could not set fl-wishlist-email in this.sessionStorage');
    } finally {
      return this.localStorage?.getItem('fl-wishlist-email');
    }
  }

  getWishlistFromsessionStorage() {
    try {
      return JSON.parse(this.localStorage.getItem('fl-wishlist') || '');
    } catch (err) {
      return '';
    }
  }

  setWishlistInsessionStorage(wishlist: any) {
    try {
      this.localStorage.setItem('fl-wishlist', JSON.stringify(wishlist));
      return this.getWishlistFromsessionStorage();
    } catch {}
  }

  assembleWishlistEvent(
    eventType: 'wishlist/add' | 'wishlist/remove' | 'tombstone',
    product: any,
    meta?: any,
    email?: string | null,
    id?: string | null
  ): any {
    const wishlistEvent: any = {
      eventType: eventType,
      event: {
        id: product.id,
        productId: product.id,
        productHandle: product.handle,
        title: product.title,
        minPrice: product?.min_price,
        maxPrice: product?.max_price,
        imageSrc:
          product?.images?.length > 0
            ? product?.images[0]?.src
            : product?.image,
        vendor: product?.vendor,
      },
      meta: meta ?? {},
    };
    if (email) {
      wishlistEvent.email = email;
    }
    if (!email && id) {
      wishlistEvent.wishlistId = id;
    }
    return wishlistEvent;
  }

  getWishlistByEmail(email: string) {
    return this.http.post(this.assembleWishlistApiUrl('email'), {
      email: email,
    });
  }

  getWishlistById(wishlistId: string) {
    if (wishlistId) {
      return this.http.post(this.assembleWishlistApiUrl('id'), {
        wishlistId: wishlistId,
      });
    }
    throw Error('no wishlist');
  }

  addToWishlist(
    product: any,
    meta?: any,
    email?: string | null,
    id?: string | null
  ) {
    return this.http.post(
      this.assembleWishlistApiUrl('event'),
      this.assembleWishlistEvent('wishlist/add', product, meta, email, id)
    );
  }

  addProductToWishlist(product: Product) {
    this.addToWishlist(
      product,
      null,
      this.getEmailFromStorage(),
      this.getWishlistIdFromStorage()
    )
      .pipe(tap((data) => this.wishlistSubject.next(data)))
      .subscribe((wishlist: any) => {
        this.setWishlistInsessionStorage(wishlist);
        this.setWishlistIdInStorage(wishlist?.wishlistId);
      });
  }

  addPersonaliserProductToWishlist(product: Product, meta: any) {
    this.addToWishlist(
      product,
      meta,
      this.getEmailFromStorage(),
      this.getWishlistIdFromStorage()
    )
      .pipe(tap((data) => this.wishlistSubject.next(data)))
      .subscribe((wishlist: any) => {
        this.setWishlistInsessionStorage(wishlist);
        this.setWishlistIdInStorage(wishlist?.wishlistId);
      });
  }

  removeProductFromWishlist(product: Product) {
    this.removeFromWishlist(
      product,
      null,
      this.getEmailFromStorage(),
      this.getWishlistIdFromStorage()
    )
      .pipe(tap((data) => this.wishlistSubject.next(data)))
      .subscribe((wishlist: any) => {
        this.setWishlistInsessionStorage(wishlist);
        this.setWishlistIdInStorage(wishlist?.wishlistId);
      });
  }

  removeFromWishlist(
    product: any,
    meta?: any,
    email?: string | null,
    id?: string | null
  ) {
    return this.http
      .post(
        this.assembleWishlistApiUrl('event'),
        this.assembleWishlistEvent('wishlist/remove', product, meta, email, id)
      )
      .pipe(tap((data) => this.wishlistSubject.next(data)));
  }

  checkProductExistsInWishlist(id: string) {
    try {
      return JSON.parse(
        this.localStorage.getItem('fl-wishlist') || '[]'
      ).events.find((x: any) => x.eventBody.productId == id);
    } catch {
      return null;
    }
  }
}
